/**
 * Facebook Click ID (fbclid) Capture Utility
 *
 * Captures the `fbclid` URL parameter from Facebook ad clicks and creates
 * a properly formatted `_fbc` cookie for improved event attribution.
 *
 * Facebook Cookie Format: fb.1.{timestamp_ms}.{fbclid_value}
 * - fb: Prefix (Facebook standard)
 * - 1: Version (always 1)
 * - timestamp_ms: Milliseconds since epoch when fbclid was captured
 * - fbclid_value: The click ID from the URL parameter
 *
 * Expiry: 90 days (matching Facebook's attribution window)
 *
 * @see https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
 */

/**
 * Helper function to get existing _fbc cookie value
 */
const getExistingFbcCookie = (): string | null => {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === '_fbc') {
            return value;
        }
    }
    return null;
};

/**
 * Helper function to extract fbclid from _fbc cookie value
 * Format: fb.1.{timestamp}.{fbclid}
 */
const extractFbclidFromCookie = (fbcValue: string): string | null => {
    const parts = fbcValue.split('.');
    // Valid format: fb.1.{timestamp}.{fbclid}
    if (parts.length === 4 && parts[0] === 'fb' && parts[1] === '1') {
        return parts[3]; // Return the fbclid part
    }
    return null;
};

/**
 * Captures fbclid from URL and creates/updates _fbc cookie
 *
 * This function implements Facebook's recommended approach:
 * 1. Checks if fbclid parameter exists in current URL
 * 2. Checks if existing _fbc cookie contains the same fbclid
 * 3. Only creates/updates cookie if fbclid is new or different (last-click attribution)
 * 4. Preserves original timestamp for repeated visits from same ad click
 *
 * @returns void
 *
 * @example
 * // First visit: https://ouronyx.com/?fbclid=AbCdEfGh123
 * captureFacebookClickId();
 * // Creates cookie: _fbc=fb.1.1729000000000.AbCdEfGh123
 *
 * // Second visit (same fbclid): https://ouronyx.com/?fbclid=AbCdEfGh123
 * captureFacebookClickId();
 * // Preserves existing cookie (keeps original timestamp)
 *
 * // Third visit (different fbclid): https://ouronyx.com/?fbclid=XyZ789NewClick
 * captureFacebookClickId();
 * // Updates cookie: _fbc=fb.1.1729123456789.XyZ789NewClick
 */
export const captureFacebookClickId = (): void => {
    // Only run client-side
    if (typeof window === 'undefined') {
        return;
    }

    try {
        // Extract fbclid from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const fbclid = urlParams.get('fbclid');

        // No fbclid in URL - nothing to capture
        if (!fbclid) {
            return;
        }

        // Check for existing _fbc cookie
        const existingFbc = getExistingFbcCookie();

        if (existingFbc) {
            // Extract fbclid from existing cookie
            const existingFbclid = extractFbclidFromCookie(existingFbc);

            // If existing cookie contains the same fbclid, preserve it
            // This maintains the original click timestamp per Facebook's guidance
            if (existingFbclid === fbclid) {
                if (process.env.NODE_ENV === 'development') {
                    console.log('[Facebook Click ID] Preserved existing cookie (same fbclid)');
                }
                return;
            }
        }

        // Generate timestamp (milliseconds since epoch)
        const timestamp = Date.now();

        // Create properly formatted _fbc cookie value
        // Format: fb.1.{timestamp}.{fbclid}
        const fbcValue = `fb.1.${timestamp}.${fbclid}`;

        // Set cookie with 90-day expiry (matching Facebook's attribution window)
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 90);

        // Set cookie with proper attributes
        // - expires: 90 days from now
        // - path: / (available across entire site)
        // - SameSite=Lax (allows cross-site navigation from Facebook)
        // - Secure: Only on HTTPS (production)
        document.cookie = `_fbc=${fbcValue};expires=${expiryDate.toUTCString()};path=/;SameSite=Lax${
            window.location.protocol === 'https:' ? ';Secure' : ''
        }`;

        // Development logging
        if (process.env.NODE_ENV === 'development') {
            console.group('[Facebook Click ID] Captured');
            console.log('fbclid:', fbclid);
            console.log('Timestamp:', timestamp);
            console.log('Cookie value:', fbcValue);
            console.log('Expiry date:', expiryDate.toUTCString());
            console.log('Action:', existingFbc ? 'Updated (new click)' : 'Created (first click)');
            console.groupEnd();
        }
    } catch (error) {
        // Silently fail to avoid disrupting user experience
        // Log error for debugging purposes
        if (process.env.NODE_ENV === 'development') {
            console.warn('[Facebook Click ID] Failed to capture:', error);
        }
    }
};
